import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';

import { nsSharedStyles, nsLayoutStyles } from '../ns-styles/index.js';

export interface Option {
  value: string;
  label: string;
}

/**
 * Search-and-pick-many combobox: selected items render as removable chips inline in the input
 * itself, with a dropdown of consumer-supplied candidate `options` underneath. Mirrors
 * a controlled-component idiom (parent owns `selected`/`options`, this just renders +
 * dispatches), split into two events since there are two distinct things to report —
 * `search-input` for the typed query (the consumer already owns its own debounce/query, e.g. against
 * Buttress) and `selection-changed` for the resulting chip set.
 */
@customElement('ns-multi-select')
export class NSMultiSelect extends LitElement {
  @query('#wrapper')
  private __wrapper?: HTMLElement;

  @query('#options-block')
  private __optionsBlock?: HTMLElement;

  @query('#search-input')
  private __input?: HTMLInputElement;

  @property({ type: Array })
  options?: Option[];

  @property({ type: Array })
  selected: Option[] = [];

  @property({ type: String })
  placeholder?: string;

  @property({ type: String })
  label?: string;

  @property({ type: String })
  searchStatus?: string;

  @property({ type: Number, attribute: 'page-size' })
  pageSize?: number;

  @state()
  private __visibleCount = 0;

  @state()
  private __query = '';

  @state()
  private __open = false;

  @state()
  private __closing = false;

  @state()
  private __activeIndex = 0;

  private __closeCount = 0;

  // When set, disables the browser's native autofill/autocomplete-from-memory suggestions on the
  // search input -- useful for a component that's always searching a live candidate list rather
  // than repeating past free-text entries.
  @property({ type: Boolean, attribute: 'no-auto-fill' })
  noAutofill?: boolean;

  private __outsideClickListener = (e: Event) => {
    const path = e.composedPath();
    if (this.__wrapper && !path.includes(this.__wrapper) && this.__open) {
      this.__closeDropdown();
    }
  };

  private __focusOutListener = (e: FocusEvent) => {
    if (e.relatedTarget === this || !this.__open) return;
    if (!e.relatedTarget) {
      setTimeout(() => {
        if (this.__open && !document.hasFocus()) this.__closeDropdown();
      });
      return;
    }
    this.__closeDropdown();
  };

  static styles = [
    nsSharedStyles,
    nsLayoutStyles,
    css`
      :host {
        display: block;
        position: relative;
      }

      #wrapper {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: var(--wa-space-2xs);

        position: relative;

        width: 100%;
        box-sizing: border-box;
        min-height: 25px;

        padding: var(--wa-space-2xs) var(--wa-space-xs);

        background-color: var(--primary-input-background-color);
        color: var(--primary-input-text-color);
        border: none;
        border-radius: var(--wa-border-radius-l);

        z-index: 2;
      }
      #wrapper:focus-within {
        outline: 2px solid var(--primary-color);
        outline-offset: 2px;
      }

      .chip {
        display: inline-flex;
        align-items: center;
        gap: var(--wa-space-2xs);

        background: var(--ns-theme-green);
        color: var(--ns-theme-black);

        padding: var(--wa-space-3xs) var(--wa-space-2xs) var(--wa-space-3xs) var(--wa-space-xs);
        border-radius: var(--wa-border-radius-l);

        font-size: 0.85em;
        line-height: 1.6;

        white-space: normal;
        max-width: 100%;
        min-width: 0;
        overflow-wrap: anywhere;
      }

      .chip button {
        display: inline-flex;
        align-items: center;
        justify-content: center;

        width: 16px;
        height: 16px;
        flex-shrink: 0;
        padding: 0;

        background: none;
        border: none;
        border-radius: var(--wa-border-radius-circle);
        color: inherit;
        cursor: pointer;

        font-size: 1em;
        line-height: 1;
      }
      .chip button:hover,
      .chip button:focus-visible {
        background: color-mix(in srgb, var(--ns-theme-black) 15%, transparent);
      }

      #search-input {
        flex: 1;
        min-width: 80px;

        background: none;
        border: none;
        outline: none;

        color: inherit;
        font-size: 1em;
        line-height: 1em;
      }
      #search-input::placeholder {
        color: inherit;
        opacity: 0.7;
      }

      #options-block {
        position: absolute;
        top: calc(100% + 4px);
        left: 0;

        background: var(--wa-color-surface-raised);
        color: var(--wa-color-text-normal);
        border-radius: var(--wa-border-radius-m);
        box-shadow: var(--ns-elevation-4);
        overflow: hidden;

        max-height: 0;
        width: 100%;

        z-index: 3;
      }
      #options-block.open {
        overflow-y: auto;
      }

      .status {
        padding: var(--wa-space-xs);
        color: var(--wa-color-text-quiet);
        font-size: 0.85em;
      }

      .option {
        display: flex;
        align-items: center;
        gap: var(--wa-space-xs);

        padding: var(--wa-space-xs);
        cursor: pointer;
        overflow-wrap: anywhere;
      }

      .option:hover,
      .option:focus,
      .option:focus-visible {
        background: var(--wa-color-brand-fill-quiet);

        outline: 2px solid var(--wa-color-focus);
        outline-offset: -2px;
      }

      .option .check {
        display: inline-flex;
        align-items: center;
        justify-content: center;

        width: 16px;
        height: 16px;

        border: 1px solid currentColor;
        border-radius: var(--wa-border-radius-s);

        flex: 0 0 auto;
      }
      .option[aria-selected='true'] .check {
        background: var(--wa-color-success-fill-loud);
        color: var(--wa-color-success-on-loud);
      }
      .option[aria-selected='true'] .check::after {
        content: '✓';
        font-size: 0.75em;
      }
    `,
  ];

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('click', this.__outsideClickListener);
    this.addEventListener('focusout', this.__focusOutListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('click', this.__outsideClickListener);
    this.removeEventListener('focusout', this.__focusOutListener);
  }

  private __isSelected(option: Option): boolean {
    return this.selected.some((s) => s.value === option.value);
  }

  private get __isPaged(): boolean {
    return !!this.pageSize && !this.__query.trim();
  }

  private get __visibleOptions(): Option[] {
    const options = this.options ?? [];
    return this.__isPaged ? options.slice(0, this.__visibleCount) : options;
  }

  private get __hasMore(): boolean {
    return this.__isPaged && this.__visibleCount < (this.options?.length ?? 0);
  }

  private __showMore() {
    if (this.__hasMore) this.__visibleCount += this.pageSize ?? 0;
  }

  private __resetPaging() {
    this.__visibleCount = this.pageSize ?? 0;
    this.__activeIndex = 0;
    // After the render: a still-`hidden` #options-block ignores scrollTop.
    this.updateComplete.then(() => {
      if (this.__optionsBlock) this.__optionsBlock.scrollTop = 0;
    });
  }

  private __onOptionsScroll(e: Event) {
    const block = e.currentTarget as HTMLElement;
    // Load the next page roughly one option before the bottom so it's ready as it comes into view.
    if (block.scrollTop + block.clientHeight >= block.scrollHeight - 40) this.__showMore();
  }

  private __openDropdown() {
    if (this.__open) return;
    this.__open = true;
    this.__activeIndex = 0;
    this.__resetPaging();
    this.__animateOptions();
  }

  private async __closeDropdown() {
    if (!this.__open) return;
    this.__open = false;
    this.__closing = true;
    // A later close (after a quick reopen) owns __closing; an earlier one finishing mustn't clear it.
    const closeId = ++this.__closeCount;
    await this.__animateOptions(true);
    if (closeId === this.__closeCount) this.__closing = false;
  }

  private __onInput(evt: InputEvent) {
    const target = evt.target as HTMLInputElement;
    this.__query = target.value;
    this.__resetPaging();
    this.__openDropdown();
    this.dispatchEvent(
      new CustomEvent('search-input', { detail: { query: target.value }, bubbles: true, composed: true }),
    );
  }

  private __onInputKeydown(e: KeyboardEvent) {
    if (e.key === 'Backspace' && !this.__input?.value && this.selected.length > 0) {
      this.__removeOption(this.selected[this.selected.length - 1]);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.__openDropdown();
      this.updateComplete.then(() => this.__optionsBlock?.querySelector<HTMLElement>('[role="option"]')?.focus());
      return;
    }
    if (e.key === 'Escape' && this.__open) {
      e.preventDefault();
      this.__closeDropdown();
    }
  }

  private __toggleOption(option: Option) {
    if (this.__isSelected(option)) {
      this.__removeOption(option);
    } else {
      this.__addOption(option);
    }
  }

  private __addOption(option: Option) {
    const selected = [...this.selected, option];
    this.selected = selected;
    if (this.__input) this.__input.value = '';
    this.__query = '';
    this.dispatchEvent(new CustomEvent('selection-changed', { detail: { selected }, bubbles: true, composed: true }));
    this.dispatchEvent(new CustomEvent('search-input', { detail: { query: '' }, bubbles: true, composed: true }));
    this.__input?.focus();
  }

  private __removeOption(option: Option) {
    const selected = this.selected.filter((s) => s.value !== option.value);
    this.selected = selected;
    this.dispatchEvent(new CustomEvent('selection-changed', { detail: { selected }, bubbles: true, composed: true }));
    this.__input?.focus();
  }

  private __onOptionKeydown(e: KeyboardEvent, option: Option, index: number) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.__toggleOption(option);
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      this.__input?.focus();
      this.__closeDropdown();
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const options = this.__optionsBlock?.querySelectorAll<HTMLElement>('[role="option"]');
      if (!options?.length) return;
      // At the last loaded option, load the next page and move into it instead of wrapping to the top.
      if (e.key === 'ArrowDown' && index === options.length - 1 && this.__hasMore) {
        this.__showMore();
        this.updateComplete.then(() => {
          this.__activeIndex = index + 1;
          this.__optionsBlock?.querySelectorAll<HTMLElement>('[role="option"]')[index + 1]?.focus();
        });
        return;
      }
      // From the first option, load the rest and wrap to the real last one rather than the last loaded.
      if (e.key === 'ArrowUp' && index === 0 && this.__hasMore) {
        this.__visibleCount = this.options?.length ?? 0;
        this.updateComplete.then(() => {
          const all = this.__optionsBlock?.querySelectorAll<HTMLElement>('[role="option"]');
          if (!all?.length) return;
          this.__activeIndex = all.length - 1;
          all[all.length - 1].focus();
        });
        return;
      }
      const next = e.key === 'ArrowDown' ? (index + 1) % options.length : (index - 1 + options.length) % options.length;
      this.__activeIndex = next;
      options[next]?.focus();
    }
  }

  private async __animateOptions(close: boolean = false) {
    const block = this.__optionsBlock;
    if (!block) return;

    block.classList.remove('open');
    const timing: KeyframeAnimationOptions = {
      duration: 300,
      easing: 'cubic-bezier(0.23, 1, 0.32, 1)',
      fill: 'forwards',
    };

    const from = `${block.getBoundingClientRect().height}px`;
    const to = close ? '0px' : '200px';

    await block.animate([{ maxHeight: from }, { maxHeight: to }], timing).finished;

    if (!close && this.__open) block.classList.add('open');
  }

  render() {
    const showDropdown =
      (this.__open || this.__closing) && (this.searchStatus || (this.options && this.options.length > 0));

    return html`
      <div id="wrapper">
        ${repeat(
          this.selected,
          (option) => option.value,
          (option) => html`
            <span class="chip">
              ${option.label}
              <button
                type="button"
                aria-label="Remove ${option.label}"
                @click=${(e: Event) => {
                  e.stopPropagation();
                  this.__removeOption(option);
                }}
              >
                ×
              </button>
            </span>
          `,
        )}
        <input
          id="search-input"
          type="text"
          placeholder="${this.selected.length === 0 ? (this.placeholder ?? '') : ''}"
          aria-label="${this.label ?? this.placeholder ?? 'Search'}"
          aria-expanded="${this.__open}"
          aria-controls="options-block"
          role="combobox"
          autocomplete="${this.noAutofill ? 'off' : nothing}"
          @input=${this.__onInput}
          @focus=${this.__openDropdown}
          @keydown=${this.__onInputKeydown}
          @click=${this.__openDropdown}
        />
      </div>

      <div
        id="options-block"
        role="listbox"
        aria-multiselectable="true"
        aria-label="${this.label ?? this.placeholder ?? 'Options'}"
        ?hidden=${!showDropdown}
        @scroll=${this.__onOptionsScroll}
      >
        ${this.searchStatus
          ? html`
              <div class="status" role="option" aria-disabled="true" aria-selected="false">${this.searchStatus}</div>
            `
          : this.options
            ? repeat(
                this.__visibleOptions,
                (option) => option.value,
                (option, index) => html`
                  <div
                    class="option"
                    role="option"
                    tabindex="${this.__open && index === this.__activeIndex ? '0' : '-1'}"
                    aria-setsize="${this.options!.length}"
                    aria-posinset="${index + 1}"
                    aria-selected="${this.__isSelected(option)}"
                    @click=${() => this.__toggleOption(option)}
                    @keydown=${(e: KeyboardEvent) => this.__onOptionKeydown(e, option, index)}
                  >
                    <span class="check"></span>
                    ${option.label}
                  </div>
                `,
              )
            : nothing}
      </div>
    `;
  }
}
